import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '@/store';
import {
  setProjectData,
  setProjectUnits,
  setProjectLeases,
  setActionItems,
  setProcesses,
} from '@/features/project/projectSlice';

// Data fetching
import {
  getProjectById,
  getUnitsByProjectId,
  getActionsByProjectId,
  getProcesses,
  getLeases,
} from '@/data';
import { Process, Lease, ActionItem } from '@urbanmix-tech/shared-js';

// Wizard components
import { usePricingWizardState } from '@/components/PricingWizard/hooks/usePricingWizardState';
import { WIZARD_STEPS } from '@/components/PricingWizard/types';
import { UnitSelectionStep } from '@/components/PricingWizard/steps/UnitSelectionStep';
import { PricingStep } from '@/components/PricingWizard/steps/PricingStep';
import { ReviewStep } from '@/components/PricingWizard/steps/ReviewStep';
import { ApprovalStep } from '@/components/PricingWizard/steps/ApprovalStep';

// Building visualization
import BuildingModelGeneric from '@/components/BuildingModel/BuildingModelGeneric';

export default function PricingWizardPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId, buildingId } = useParams<{
    projectId: string;
    buildingId: string;
  }>();

  const [loading, setLoading] = useState(true);
  const [hoveredUnitId, setHoveredUnitId] = useState<string | null>(null);

  // Redux selectors
  const project = useSelector((state: RootState) => state.project.project);
  const units = useSelector((state: RootState) => state.project.projectUnits);
  const allLeases = useSelector((state: RootState) => state.project.projectLeases);
  const actionItems = useSelector((state: RootState) => state.project.actionItems);
  const processes = useSelector((state: RootState) => state.project.processes);

  // Get the building from project
  const building = useMemo(() => {
    if (!project?.buildings || !buildingId) return null;
    return project.buildings.find((b) => String(b.id) === buildingId) || project.buildings[0];
  }, [project, buildingId]);

  // Filter to valid leases for pricing
  const validLeases = useMemo(() => {
    return allLeases.filter((lease) => lease.status === 'valid');
  }, [allLeases]);

  // Draft leases created during this wizard session
  const draftLeases = useMemo(() => {
    return allLeases.filter((lease) => lease.status === 'draft');
  }, [allLeases]);

  // Wizard state - pass draft leases to initialize from existing drafts
  const wizard = usePricingWizardState(units, validLeases, draftLeases);

  // Load project data
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) return;

      setLoading(true);

      try {
        // Load project if not already loaded
        if (!project || project.id !== projectId) {
          const projectData = await getProjectById(projectId);
          if (projectData) {
            dispatch(setProjectData(projectData));
          }
        }

        // Load units if not already loaded
        if (units.length === 0) {
          const loadedUnits = await getUnitsByProjectId(projectId);
          dispatch(setProjectUnits(loadedUnits));
        }

        // Load action items
        const projectActions = await getActionsByProjectId(projectId);
        dispatch(setActionItems(projectActions));

        // Subscribe to processes
        getProcesses({ projectId }, (processes: Process[]) => {
          dispatch(setProcesses(processes));
        });

        // Subscribe to leases
        getLeases({ projectId }, (leases: Lease[]) => {
          dispatch(setProjectLeases(leases));
        });
      } catch (error) {
        console.error('Error loading wizard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, dispatch]);

  const handleBack = () => {
    navigate(`/m/projects/${projectId}/${buildingId}`);
  };

  const handleStepClick = (stepIndex: number) => {
    // Only allow clicking on steps that have been completed or are current
    if (stepIndex <= wizard.state.activeStep) {
      wizard.actions.setActiveStep(stepIndex);
    }
  };

  // Building model handlers
  const handleSelectUnit = useCallback(
    (unitId: string | null) => {
      if (unitId && wizard.state.activeStep === 0) {
        // In step 1, clicking toggles selection
        wizard.actions.toggleUnitSelection(unitId);
      }
    },
    [wizard.state.activeStep, wizard.actions]
  );

  const handleHoverUnit = useCallback((unitId: string | null) => {
    setHoveredUnitId(unitId);
  }, []);

  // Get selected unit IDs as array for building model
  const selectedUnitIdsArray = useMemo(() => {
    return Array.from(wizard.state.selectedUnitIds);
  }, [wizard.state.selectedUnitIds]);

  const canProceedToNextStep = () => {
    switch (wizard.state.activeStep) {
      case 0: // Select Units
        return wizard.totalSelectedCount > 0;
      case 1: // Price Units
        return wizard.pricedUnitsCount > 0;
      case 2: // Review
        return true;
      case 3: // Approve
        return true;
      default:
        return false;
    }
  };

  if (loading) {
    return (
      <Box className="flex items-center justify-center h-full">
        <CircularProgress />
        <Typography className="ml-4">Loading pricing wizard...</Typography>
      </Box>
    );
  }

  return (
    <div className="pricing-wizard-page flex h-full">
      {/* Left side: Wizard content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-zinc-700">
          <div className="flex items-center gap-4">
            {/* Back button */}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              variant="text"
              size="medium"
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Back
            </Button>

            {/* Inline stepper */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {WIZARD_STEPS.map((label, index) => {
                const isCompleted = index < wizard.state.activeStep;
                const isCurrent = index === wizard.state.activeStep;
                const isClickable = index <= wizard.state.activeStep;

                return (
                  <div key={label} className="flex items-center">
                    {index > 0 && (
                      <div
                        className={`w-6 h-px mx-1 ${
                          index <= wizard.state.activeStep ? 'bg-purple-500' : 'bg-zinc-600'
                        }`}
                      />
                    )}
                    <button
                      onClick={() => handleStepClick(index)}
                      disabled={!isClickable}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm transition-colors ${
                        isCurrent
                          ? 'bg-purple-600/30 text-purple-300 font-semibold'
                          : isCompleted
                          ? 'text-purple-400 hover:bg-zinc-700/50 cursor-pointer'
                          : 'text-zinc-500 cursor-default'
                      }`}
                    >
                      <span
                        className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                          isCompleted
                            ? 'bg-purple-600 text-white'
                            : isCurrent
                            ? 'bg-purple-600 text-white'
                            : 'bg-zinc-700 text-zinc-400'
                        }`}
                      >
                        {isCompleted ? <CheckIcon sx={{ fontSize: 14 }} /> : index + 1}
                      </span>
                      <span>{label}</span>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Navigation buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outlined"
                onClick={wizard.actions.previousStep}
                disabled={wizard.state.activeStep === 0}
                size="medium"
              >
                Previous
              </Button>
              {wizard.state.activeStep < 3 ? (
                <Button
                  variant="contained"
                  onClick={wizard.actions.nextStep}
                  disabled={!canProceedToNextStep()}
                  size="medium"
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleBack}
                  size="medium"
                >
                  Finish
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Step content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {wizard.state.activeStep === 0 && (
            <UnitSelectionStep
              units={units}
              leases={validLeases}
              actionItems={actionItems}
              wizard={wizard}
            />
          )}
          {wizard.state.activeStep === 1 && (
            <PricingStep
              units={units}
              leases={validLeases}
              allLeases={allLeases}
              actionItems={actionItems}
              wizard={wizard}
            />
          )}
          {wizard.state.activeStep === 2 && (
            <ReviewStep
              units={units}
              leases={validLeases}
              draftLeases={draftLeases}
              wizard={wizard}
              projectId={projectId || ''}
              buildingId={building?.id || 0}
            />
          )}
          {wizard.state.activeStep === 3 && (
            <ApprovalStep
              units={units}
              draftLeases={draftLeases}
              wizard={wizard}
              onComplete={handleBack}
            />
          )}
        </div>
      </div>

      {/* Right side: Building model visualization */}
      <div className="w-1/3 h-full bg-zinc-900 border-l border-zinc-700">
        {building && projectId ? (
          <BuildingModelGeneric
            projectId={projectId}
            building={building}
            units={units}
            selectedUnitIds={selectedUnitIdsArray}
            onSelectUnit={handleSelectUnit}
            onHoverUnit={handleHoverUnit}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Typography variant="body2" color="textSecondary">
              Loading building model...
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
